import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pilabRequestsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  ImprovementRequestFilter,
  ImprovementRequestInput,
  ImprovementResolveInput,
} from '../lib/api/pilab-requests.js';

/**
 * 改善要望の一覧を取る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PI Lab への改善要望を状態・出した人で絞って取得する
 * @graph-connects /pilab-requests [calls] 要望の一覧取得API
 * @graph-connects pilabRequestsApi.list [calls] 改善要望APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePilabRequests(filter: ImprovementRequestFilter = {}) {
  return useQuery({
    queryKey: queryKeys.pilabRequests.list(filter),
    queryFn: () => pilabRequestsApi.list(filter),
  });
}

/**
 * 一覧を読み直す。
 *
 * 出す・判断するはどちらも一覧の状態を動かすので、**絞り込みを問わず全部**
 * 無効化する（出した直後の要望は未振り分けなので、今見ている絞り込みには入らない）。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useRefreshPilabRequests(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.pilabRequests.all });
  };
}

/**
 * 改善要望を出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PI Lab への改善要望を登録する
 * @graph-connects /pilab-requests [calls] 要望の登録API
 * @graph-connects pilabRequestsApi.create [calls] 改善要望APIクライアント呼び出し
 * @graph-connects useRefreshPilabRequests [calls] 登録後の一覧再取得
 */
export function useCreatePilabRequest() {
  const refresh = useRefreshPilabRequests();
  return useMutation({
    mutationFn: (input: ImprovementRequestInput) => pilabRequestsApi.create(input),
    onSuccess: refresh,
  });
}

/**
 * 出した人が要望を書き直す。
 *
 * **書き直すと AI の振り分けがやり直しになる**（判断待ち → 未振り分け）ので、一覧の状態が
 * 変わる。絞り込みを問わず読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 判断待ちの改善要望を書き直し、AI の振り分けをやり直させる
 * @graph-connects /pilab-requests/{id} [calls] 要望の書き直しAPI
 * @graph-connects pilabRequestsApi.update [calls] 改善要望APIクライアント呼び出し
 * @graph-connects useRefreshPilabRequests [calls] 書き直し後の一覧再取得
 */
export function useUpdatePilabRequest() {
  const refresh = useRefreshPilabRequests();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ImprovementRequestInput }) =>
      pilabRequestsApi.update(id, input),
    onSuccess: refresh,
  });
}

/**
 * 人の判断を記録する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望に人の判断（やる・やらない・未解決に戻す）を記録する
 * @graph-connects /pilab-requests/{id}/resolve [calls] 判断の記録API
 * @graph-connects pilabRequestsApi.resolve [calls] 改善要望APIクライアント呼び出し
 * @graph-connects useRefreshPilabRequests [calls] 記録後の一覧再取得
 */
export function useResolvePilabRequest() {
  const refresh = useRefreshPilabRequests();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ImprovementResolveInput }) =>
      pilabRequestsApi.resolve(id, input),
    onSuccess: refresh,
  });
}

/**
 * 要望をチーム課題に移す（要望は「課題に移動」で閉じる）。移した先の課題もボードに増えるので、
 * 組織課題の表示も読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望を選んだ組織課題ボードの課題に移し、要望を閉じる
 * @graph-connects /pilab-requests/{id}/move-to-issue [calls] チーム課題への移動API
 * @graph-connects pilabRequestsApi.moveToIssue [calls] 改善要望APIクライアント呼び出し
 * @graph-connects useRefreshPilabRequests [calls] 移動後の一覧再取得
 * @graph-connects queryKeys [calls] 組織課題の再取得キー
 */
export function useMovePilabRequestToIssue() {
  const refresh = useRefreshPilabRequests();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, boardId }: { id: string; boardId: string }) =>
      pilabRequestsApi.moveToIssue(id, { boardId }),
    onSuccess: async () => {
      await Promise.all([
        refresh(),
        client.invalidateQueries({ queryKey: queryKeys.orgIssues.all }),
      ]);
    },
  });
}

/**
 * 振り分けの基準を取る。
 *
 * 画面に出すのは、**出す人が「これはどう扱われるのか」を先に読めるようにする**ため。
 * 基準を読まずに出した要望は却下されたときに理由が分からず、同じものが再び出る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望の振り分け基準（Markdown）を取得する
 * @graph-connects /pilab-requests/guideline [calls] 振り分け基準の取得API
 * @graph-connects pilabRequestsApi.guideline [calls] 改善要望APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePilabTriageGuideline(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.pilabRequests.guideline(),
    queryFn: () => pilabRequestsApi.guideline(),
    enabled: options.enabled ?? true,
  });
}

/**
 * 改善要望に付けるスクショをアップロードして URL を得る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望に添付するスクショを保存し、要望に入れる配信 URL を得る
 * @graph-connects /pilab-requests/attachments [calls] スクショのアップロードAPI
 * @graph-connects pilabRequestsApi.uploadAttachment [calls] 改善要望APIクライアント呼び出し
 */
export function useUploadPilabAttachment() {
  return useMutation({
    mutationFn: (input: { contentType: string; dataBase64: string }) =>
      pilabRequestsApi.uploadAttachment(input),
  });
}
